import { toggleCaptchaBadge } from '@/App'
import { privacyPolicyLink, termsAndPoliciesLink } from '@/_shared/constants'
import SlotPageWrapper from '@/components/SlotPageWrapper'
import { Text } from '@/components/custom-components'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label.tsx'
import { PasswordInput } from '@/components/shadcn/password-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import usePasswordStrengthCheck from '@/hooks/usePasswordStrengthCheck.tsx'
import showToastNotification from '@/hooks/useShowToast'
import { trpc } from '@/utils/trpc.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check, X } from '@mynaui/icons-react'
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect, useLayoutEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useLocalStorage } from 'usehooks-ts'
import { z } from 'zod'

export function isMobileDeviceMetaBrowser() {
  // User-Agent detection for common mobile platforms
  const mobileUserAgentRegex = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|Windows Phone|Opera Mini|IEMobile/i

  // Feature detection (touchscreen)
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0

  // Screen size detection
  const isSmallScreen = window.matchMedia('(max-width: 768px)').matches
  // check if it is facebook or instagrams browser
  const ua = navigator.userAgent
  const metaBrowserTags = ['FBAN', 'FBAV', 'Instagram']

  // Combine checks for robustness
  return (
    (mobileUserAgentRegex.test(ua) || (hasTouchScreen && isSmallScreen)) &&
    metaBrowserTags.some((tag) => ua.includes(tag))
  )
}

type SearchParams = {
  plan?: string
  coupon?: string
  redirect?: string
}

export const Route = createFileRoute('/sign-up')({
  beforeLoad: ({ context }) => {
    const { auth } = context
    if (auth.isAuthenticated && auth.isAuthenticatedNextRoute) {
      throw redirect({
        to: auth.isAuthenticatedNextRoute,
        search: { from: 'sign-up' },
      })
    }
  },
  component: SignUp,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const plan = search?.plan as string | undefined
    const coupon = search?.coupon as string | undefined

    return {
      plan,
      coupon,
    }
  },
})

function SignUp() {
  const { plan, coupon, redirect } = Route.useSearch()
  const navigate = useNavigate()
  const [, set_cos_plan] = useLocalStorage<string>('cos_plan', '')
  const [, set_cos_coupon] = useLocalStorage<string>('cos_coupon', '')

  const { passwordColor, passwordStrengthLabel, passwordStrength, handlePasswordChange } = usePasswordStrengthCheck()

  useLayoutEffect(() => {
    document.body.classList.toggle('dark', false)
  }, [])

  useEffect(() => {
    toggleCaptchaBadge(true)
    return () => toggleCaptchaBadge(false) // Hide badge when navigating away
  }, [])

  useEffect(() => {
    if (plan) {
      set_cos_plan(plan)
    }
    if (coupon) {
      set_cos_coupon(coupon)
    }
  }, [])

  const { mutate: signUpWithEmailAndPasswordWorkOs, isPending } = trpc.signUpWithEmailAndPassword.useMutation()

  const { mutate: signUpWithGoogle, isPending: isPendingGoogleUrl } = trpc.generateGoogleAuthorizationUrl.useMutation()

  const { mutate: createRecaptchaAssessment } = trpc.createRecaptchaAssessment.useMutation()

  const signupFormSchema = z
    .object({
      firstName: z.string({ required_error: 'First Name is required' }),
      lastName: z.string({ required_error: 'Last Name is required' }),
      heardFrom: z
        .string()
        .refine(
          (val): val is 'search' | 'advertisement' | 'newsletter' | 'friends-or-family' =>
            ['search', 'advertisement', 'newsletter', 'friends-or-family'].includes(val),
          {
            message: 'Please select a valid option',
          },
        ),
      email: z.string({ required_error: 'Email address is required' }).email({
        message: 'Please enter a valid email address.',
      }),
      password: z
        .string({ required_error: 'Password is required' })
        .min(8, {
          message: 'Password must be at least 8 characters.',
        })
        .regex(/[a-z]/, {
          message: 'Password must contain at least one lowercase letter.',
        })
        .regex(/[A-Z]/, {
          message: 'Password must contain at least one uppercase letter.',
        })
        .regex(/\d/, { message: 'Password must contain at least one number.' }),
      newsletter: z.boolean(),
      hasAgreedToTermsAndConditions: z.boolean({
        required_error: 'You must agree to our Terms and Policies before proceeding',
      }),
    })
    .refine((data) => data.password !== data.email, {
      message: 'Password cannot be the same as your email',
      path: ['password'],
    })

  type SignupFormValues = z.infer<typeof signupFormSchema>

  const defaultValues: Partial<SignupFormValues> = {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    newsletter: true,
  }

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    defaultValues,
  })

  const password = form.watch('password') || ''

  const passwordRequirements = [
    { text: 'At least 8 characters', valid: password?.length >= 8 },
    { text: 'At least one uppercase letter', valid: /[A-Z]/.test(password) },
    { text: 'At least one lowercase letter', valid: /[a-z]/.test(password) },
    { text: 'At least one number', valid: /\d/.test(password) },
    {
      text: 'At least one special character',
      valid: /[!@#$%^&*\-=[\]{}\\|;':",.\/?]/.test(password),
    },
  ]

  async function onSubmit({ firstName, lastName, email, password, heardFrom }: SignupFormValues) {
    const signupWithEmailAndPasswordOpts = {
      onSuccess: (data: unknown) => {
        if (data) {
          localStorage.setItem(
            '_wos_user',
            JSON.stringify({
              ...data,
              newsletter: form.getValues('newsletter'),
            }),
          )
          showToastNotification('success', {
            message: 'Account created successfully!',
          })
          navigate({ to: '/verify-email', search: { redirect } })
        }
      },
      onError: (error: { message: string }) => {
        showToastNotification('error', {
          message: error.message,
        })
      },
    }

    window.grecaptcha.enterprise.ready(async () => {
      const token = await window.grecaptcha.enterprise.execute(import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY, {
        action: 'SIGN_UP',
      })

      if (token) {
        createRecaptchaAssessment({ token, action: 'SIGN_UP' })

        signUpWithEmailAndPasswordWorkOs(
          {
            firstName,
            lastName,
            email,
            password,
            plan,
            heardFrom,
          },
          signupWithEmailAndPasswordOpts,
        )
      }
    })
  }

  const signUpWithGoogleAuth = async () => {
    const signUpWithGoogleOpts = {
      onSuccess: (data: string) => {
        if (data) {
          window.location.href = data
        }
      },
      onError: (error: { message: string }) => {
        showToastNotification('error', {
          message: error.message,
        })
      },
    }
    signUpWithGoogle(undefined, signUpWithGoogleOpts)
  }

  return (
    <SlotPageWrapper>
      <div className={'flex flex-col items-center gap-2 w-full'}>
        <img alt={'CreativeOS'} src={'/images/icon_light.png'} className={'h-10'} />
        <h4 className={'text-2xl text-center text-themeforeground font-semibold'}>Create an Account</h4>
      </div>
      <div className={'flex flex-col items-center gap-2 w-full'}>
        {!isMobileDeviceMetaBrowser() && (
          <>
            <Button
              variant={'outline'}
              type={'submit'}
              disabled={isPendingGoogleUrl}
              onClick={signUpWithGoogleAuth}
              className={'flex gap-2 items-center justify-center w-full'}
            >
              <img alt={''} src={'/images/google-icon.png'} />
              <span>Sign Up with Google</span>
            </Button>
            <div className={'flex items-center self-stretch gap-3 mt-6'}>
              <span className={'border border-themeborder w-full'} />
              <span>OR</span>
              <span className={'border border-themeborder w-full'} />
            </div>
          </>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 w-full">
            <div className={'flex flex-col gap-6'}>
              <div className={'grid lg:grid-cols-2 gap-6'}>
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <Label>First Name</Label>
                      <FormControl>
                        <Input placeholder="First Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <Label>Last Name</Label>
                      <FormControl>
                        <Input placeholder="Last Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <Label>Email Address</Label>
                    <FormControl>
                      <Input placeholder="name@example.com" type={'email'} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <Label>Password</Label>
                    <FormControl>
                      <PasswordInput
                        placeholder="Password"
                        {...field}
                        onChange={(e) => {
                          handlePasswordChange(e)
                          field.onChange(e)
                        }}
                      />
                    </FormControl>
                    {form.getValues('password') && (
                      <div className="text-xs mt-1">
                        Password Strength:{' '}
                        <span
                          style={{ color: passwordColor }}
                          className={`font-bold ${passwordColor ? `text-[${passwordColor}]` : ''}`}
                        >
                          {passwordStrengthLabel}
                        </span>
                      </div>
                    )}
                    <FormMessage />
                    <div className="text-xs space-y-1">
                      {passwordRequirements.map((req: { valid: boolean; text: string }, index: number) => (
                        <div key={index} className="flex items-center gap-2">
                          {req.valid ? (
                            <Check className="text-green-500" size={16} />
                          ) : (
                            <X className="text-red-500" size={16} />
                          )}
                          <span className={req.valid ? 'text-green-500' : 'text-gray-500'}>{req.text}</span>
                        </div>
                      ))}
                    </div>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="heardFrom"
                render={({ field }) => (
                  <FormItem>
                    <Label>How did you hear about us?</Label>
                    <FormControl>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {[
                            {
                              label: 'Advertisement',
                              value: 'advertisement',
                            },
                            {
                              label: 'Newsletter',
                              value: 'newsletter',
                            },
                            {
                              label: 'Friend or Family',
                              value: 'friends-or-family',
                            },
                            { label: 'Search', value: 'search' },
                          ].map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="newsletter"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex gap-2 items-center">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked)
                          }}
                        />
                      </FormControl>
                      <Text weight={'medium'} size={'sm'}>
                        Receive useful tips, newsletters, and promotions via e-mail
                      </Text>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="hasAgreedToTermsAndConditions"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex gap-2 items-center">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked)
                          }}
                          required
                        />
                      </FormControl>
                      <Text weight={'medium'} size={'sm'}>
                        I have read and agreed to the{' '}
                        <a target="_blank" href={termsAndPoliciesLink} className="text-blue-600">
                          Terms and Policies
                        </a>{' '}
                        and{' '}
                        <a href={privacyPolicyLink} className="text-blue-600">
                          Privacy Policy
                        </a>
                      </Text>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div>
              <Button
                disabled={isPending || passwordStrength < 2 || !form.watch('hasAgreedToTermsAndConditions')}
                type={'submit'}
                className="w-full"
                loading={isPending}
              >
                {isPending ? 'Creating an account...' : 'Signup'}
              </Button>
              <div>
                <Link to={'/login'} search={{ redirect: redirect }} className={'block text-center mt-1.5 text-sm'}>
                  Already have an account? <span className={'underline font-medium'}>Sign in</span>
                </Link>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </SlotPageWrapper>
  )
}
